/**
 * useEventStream — subscribes to the backend SSE channel with managed
 * reconnect + hard back-off. Previous revisions could enter a tight-loop
 * retry on a dead pipe; this version enforces:
 *   • stop entirely on 401 (expired token — App-level will logout)
 *   • exponential reconnect delay (1s → 8s, max 16s)
 *   • drop ref when component unmounts so no stale closures linger
 *   • bail after `MAX_RECONNECTS` back-offs (prevents runaway)
 */
import { useEffect, useRef } from 'react';

const MAX_RECONNECTS = 20;

export default function useEventStream(handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  useEffect(() => {
    const token = sessionStorage.getItem('atde_token');
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/events/stream?t=${encodeURIComponent(token)}`;
    let es = null;
    let closed = false;
    let reconnectTimer = null;
    let retries = 0;

    const connect = () => {
      if (closed) return;
      try {
        es = new EventSource(url, { withCredentials: false });
      } catch {
        return;
      }
      const register = (type) => {
        es.addEventListener(type, (e) => {
          try {
            const payload = e.data ? JSON.parse(e.data) : {};
            handlersRef.current?.[type]?.(payload);
          } catch {}
        });
      };
      Object.keys(handlersRef.current || {}).forEach(register);
      ['hello', 'watchlist.snapshot', 'job.done', 'alert.new'].forEach(register);
      es.onopen = () => { retries = 0; };
      es.onerror = () => {
        if (closed) return;
        try { es?.close(); } catch {}
        es = null;
        if (retries >= MAX_RECONNECTS) {
          // Give up quietly — polling still keeps UI correct.
          return;
        }
        const delay = Math.min(1000 * Math.pow(2, retries), 16000);
        retries += 1;
        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connect, delay);
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      try { es?.close(); } catch {}
      es = null;
    };
  }, []);
}
